/**
 * IntegrationsPage
 *
 * Unified page for all project integrations. Each section renders only when the viewer's
 * subscriptions open it (`getIntegrationSectionAccess`, UI-only gates — D-11 / D-21); a viewer
 * with no section (direct link) sees the empty state.
 *
 * @module integrations/pages/IntegrationsPage
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  ExpandMore as ExpandMoreIcon,
  Dns as DomainIcon,
  CreditCard as StripeIcon,
  Payments as PaddleIcon,
  Analytics as AnalyticsIcon,
  Campaign as MetaAdsIcon,
  AdsClick as GoogleAdsIcon,
  MusicNote as TikTokAdsIcon,
  Twitter as TwitterIcon,
  Reddit as RedditIcon,
  SmartToy as AIIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useProject } from '@modules/navigation/contexts';
import { useProjectCapability } from '@modules/billing-access';
import { CustomDomainSection } from '@modules/custom-domains';
import { StripeIntegrationContent } from '@modules/stripe-connect';
import { PaddleIntegrationContent } from '@modules/paddle-connect';
import { AnalyticsIntegrationContent } from '@modules/analytics-settings';
import { MetaAdsIntegrationContent } from '@modules/meta-ads-connect';
import { GoogleAdsIntegrationContent } from '@modules/google-ads-connect';
import { TikTokAdsIntegrationSection } from '@modules/tiktok-ads-connect';
import { XTwitterIntegrationContent } from '@modules/x-twitter-connect';
import { RedditIntegrationContent } from '@modules/reddit-connect';
import { AIProvidersList } from '@modules/ai-settings/components/AIProvidersList';
import { useAIKeys } from '@modules/ai-settings/hooks/useAIKeys';
import { LocaleConfigPanel } from '@modules/locale-config';
import { WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { GUIDE_ANCHORS } from '@modules/guides/anchors/guideAnchorRegistry';
import { useGuideHighlight } from '@modules/guides/hooks/useGuideHighlight';
import { useIntegrationSectionAccess } from '../hooks/useIntegrationSectionAccess';

/** Pointer to Articles → Site & domain under the funnel-domain slot (mirrored as INTEGRATIONS.articlesDomainHint in @test-kit). */
const INTEGRATIONS_ARTICLES_DOMAIN_HINT_TEST_ID = 'integrations-articles-domain-hint';

/** Shown when the viewer's subscriptions open no section (mirrored as INTEGRATIONS.emptyState in @test-kit). */
const INTEGRATIONS_EMPTY_STATE_TEST_ID = 'integrations-empty-state';

/** LocalStorage key for accordion state */
const STORAGE_KEY = 'integrations-accordion-state';

/**
 * Load expanded panels from localStorage
 */
function loadExpandedState(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return []; // Default: all collapsed
}

/**
 * Save expanded panels to localStorage
 */
function saveExpandedState(expanded: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(expanded));
  } catch {
    // Ignore storage errors
  }
}

/**
 * DC-12 / AC-011 P2-P3: `?guide=` lands here from the checklist; the hook clicks the collapsed
 * accordion summary, which flows through handleAccordionChange → localStorage. Mounted only once the
 * sections render: its anchor lookup is bounded (~5 s), so it must not run against the loading spinner
 * while the access snapshot is still on its way.
 */
function GuideHighlightOnSections(): null {
  useGuideHighlight();
  return null;
}

/**
 * IntegrationsPage Component
 *
 * Combines all integration settings into a single page with collapsible sections:
 * 1. Custom Domain - for custom subdomain configuration
 * 2. Stripe - for payment processing (OAuth Connect)
 * 3. Paddle - for payment processing (Merchant of Record)
 * 4. Analytics - for tracking (GDPR consent is now per-funnel)
 * 5. Meta Ads - for paid acquisition analytics validation
 * 6. Google Ads - for read-only advertiser reporting account selection
 * 7. TikTok Ads - for read-only advertiser, entity, and delivery reporting
 *
 * Accordion state is persisted to localStorage.
 */
export function IntegrationsPage() {
  const { projectId, isLoading } = useProject();
  const sectionAccess = useIntegrationSectionAccess(projectId ?? undefined);
  const { sections } = sectionAccess;
  // Only load AI keys when the section is open — no provider calls for viewers without it.
  const aiKeys = useAIKeys(sections['ai-keys'] ? projectId ?? undefined : undefined);
  const guideService = useMemo(() => new GuideService(), []);
  // The article domain moved to Articles → Site & domain (S-4); the pointer is for
  // projects that can open that page (D-6).
  const articlesAccess = useProjectCapability(projectId ?? undefined, 'articles.access');

  // Track which accordions are expanded (load from localStorage, default all collapsed)
  const [expanded, setExpanded] = useState<string[]>(loadExpandedState);

  // Persist state to localStorage when it changes
  useEffect(() => {
    saveExpandedState(expanded);
  }, [expanded]);

  const handleAccordionChange = useCallback(
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(prev =>
        isExpanded
          ? [...prev, panel]
          : prev.filter(p => p !== panel)
      );
    },
    []
  );

  if (isLoading || sectionAccess.loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!projectId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a project to configure integrations.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }} data-testid="integrations-page">
      <GuideHighlightOnSections />
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h1">
            Integrations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure domain, payments, analytics, advertising, and social integrations for your project.
          </Typography>
        </Box>
      </Box>

      {!sectionAccess.hasAny && (
        <Box
          data-testid={INTEGRATIONS_EMPTY_STATE_TEST_ID}
          sx={{ textAlign: 'center', py: 6, px: 2 }}
        >
          <Typography variant="body1" color="text.secondary">
            No integrations are available on this plan.
          </Typography>
          <Link
            component={RouterLink}
            to={`/project/${projectId}/billing`}
            variant="body2"
            sx={{ display: 'inline-block', mt: 1 }}
          >
            View plans in Billing
          </Link>
        </Box>
      )}

      {/* Section 1: Custom Domain */}
      {sections.domain && (
        <Accordion
          expanded={expanded.includes('domain')}
          onChange={handleAccordionChange('domain')}
          sx={{ mb: 2 }}
          data-testid="integrations-section-domain"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <DomainIcon color="action" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  <WithGuideHelp
                    guideKey={GUIDE_ANCHORS.customDomainSection.guideKey}
                    guideService={guideService}
                    data-testid={GUIDE_ANCHORS.customDomainSection.testId}
                    position="inline-end"
                    stopClickPropagation
                    iconComponent="span"
                  >
                    Custom Domain
                  </WithGuideHelp>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect your own subdomain to access funnels with a custom URL
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails data-testid="integrations-content-domain">
            {/* Funnel domain only: the article domain lives in Articles → Site & domain (S-4). */}
            <CustomDomainSection projectId={projectId} />
            {articlesAccess.allowed && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2 }}
                data-testid={INTEGRATIONS_ARTICLES_DOMAIN_HINT_TEST_ID}
              >
                The domain for your articles is set up in{' '}
                <Link component={RouterLink} to={`/project/${projectId}/articles/site`}>
                  Articles → Site &amp; domain
                </Link>
                .
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Section 2: Stripe Integration */}
      {sections.stripe && (
        <Accordion
          expanded={expanded.includes('stripe')}
          onChange={handleAccordionChange('stripe')}
          sx={{ mb: 2 }}
          data-testid="integrations-section-stripe"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <StripeIcon color="action" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  <WithGuideHelp
                    guideKey={GUIDE_ANCHORS.stripeConnectSection.guideKey}
                    guideService={guideService}
                    data-testid={GUIDE_ANCHORS.stripeConnectSection.testId}
                    position="inline-end"
                    stopClickPropagation
                    iconComponent="span"
                  >
                    Stripe Integration
                  </WithGuideHelp>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect your Stripe account to accept payments in funnels
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails data-testid="integrations-content-stripe">
            <StripeIntegrationContent projectId={projectId} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Section 3: Paddle Integration */}
      {sections.paddle && (
        <Accordion
          expanded={expanded.includes('paddle')}
          onChange={handleAccordionChange('paddle')}
          sx={{ mb: 2 }}
          data-testid="integrations-section-paddle"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PaddleIcon color="action" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Paddle Integration
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect your Paddle account to accept payments in funnels
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails data-testid="integrations-content-paddle">
            <PaddleIntegrationContent projectId={projectId} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Section 4: Analytics */}
      {sections.analytics && (
        <Accordion
          expanded={expanded.includes('analytics')}
          onChange={handleAccordionChange('analytics')}
          sx={{ mb: 2 }}
          data-testid="integrations-section-analytics"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <AnalyticsIcon color="action" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  <WithGuideHelp
                    guideKey={GUIDE_ANCHORS.analyticsSection.guideKey}
                    guideService={guideService}
                    data-testid={GUIDE_ANCHORS.analyticsSection.testId}
                    position="inline-end"
                    stopClickPropagation
                    iconComponent="span"
                  >
                    Analytics
                  </WithGuideHelp>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure analytics integrations for your web funnels
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails data-testid="integrations-content-analytics">
            <AnalyticsIntegrationContent projectId={projectId} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Section 5: Meta Ads */}
      {sections['meta-ads'] && (
        <Accordion
          expanded={expanded.includes('meta-ads')}
          onChange={handleAccordionChange('meta-ads')}
          sx={{ mb: 2 }}
          data-testid="integrations-section-meta-ads"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <MetaAdsIcon color="action" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Meta Ads
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect Meta Marketing API for ad account and spend analytics validation
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails data-testid="integrations-content-meta-ads">
            <MetaAdsIntegrationContent projectId={projectId} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Section 6: Google Ads */}
      {sections['google-ads'] && (
        <Accordion
          expanded={expanded.includes('google-ads')}
          onChange={handleAccordionChange('google-ads')}
          sx={{ mb: 2 }}
          data-testid="integrations-section-google-ads"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <GoogleAdsIcon color="action" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Google Ads
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose advertiser accounts for read-only campaign and spend reporting
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails data-testid="integrations-content-google-ads">
            <GoogleAdsIntegrationContent projectId={projectId} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Section 7: TikTok Ads */}
      {sections['tiktok-ads'] && (
        <Accordion
          expanded={expanded.includes('tiktok-ads')}
          onChange={handleAccordionChange('tiktok-ads')}
          sx={{ mb: 2 }}
          data-testid="integrations-section-tiktok-ads"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TikTokAdsIcon color="action" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  TikTok Ads
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select advertisers for read-only campaign, creative, and delivery reporting
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails data-testid="integrations-content-tiktok-ads">
            <TikTokAdsIntegrationSection projectId={projectId} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Section 8: X / Twitter */}
      {sections['x-twitter'] && (
        <Accordion
          expanded={expanded.includes('x-twitter')}
          onChange={handleAccordionChange('x-twitter')}
          sx={{ mb: 2 }}
          data-testid="integrations-section-x-twitter"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TwitterIcon color="action" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  X / Twitter
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect an X account for official API access
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails data-testid="integrations-content-x-twitter">
            <XTwitterIntegrationContent projectId={projectId} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Section 9: Reddit */}
      {sections.reddit && (
        <Accordion
          expanded={expanded.includes('reddit')}
          onChange={handleAccordionChange('reddit')}
          sx={{ mb: 2 }}
          data-testid="integrations-section-reddit"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <RedditIcon color="action" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Reddit
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect a Reddit account for official Data API access
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails data-testid="integrations-content-reddit">
            <RedditIntegrationContent projectId={projectId} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Section 10: AI Provider Keys */}
      {sections['ai-keys'] && (
        <Accordion
          expanded={expanded.includes('ai-keys')}
          onChange={handleAccordionChange('ai-keys')}
          sx={{ mb: 2 }}
          data-testid="integrations-section-ai-keys"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <AIIcon color="action" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  AI Provider Keys
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure API keys for AI content generation (Claude, GPT, Gemini)
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails data-testid="integrations-content-ai-keys">
            <AIProvidersList
              providers={aiKeys.providers}
              onStoreKey={aiKeys.storeKey}
              onDeleteKey={aiKeys.deleteKey}
              onValidateKey={aiKeys.validateKey}
              saving={aiKeys.saving}
              validating={aiKeys.validating}
            />
          </AccordionDetails>
        </Accordion>
      )}


      {/* Section 11: Locale Configuration — copy for Content Plan (creatives use the same project
          languages). Same component and data as in Onboarding settings on the Onboardings page (D-12). */}
      {sections['locales-content-plan'] && (
        <Accordion
          expanded={expanded.includes('locales-content-plan')}
          onChange={handleAccordionChange('locales-content-plan')}
          data-testid="integrations-section-locales-content-plan"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LanguageIcon color="action" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Locale Configuration
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage shared project languages for funnels, onboarding flows, and creatives
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails data-testid="integrations-content-locales-content-plan">
            <LocaleConfigPanel projectId={projectId} />
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}

export default IntegrationsPage;
